
import React, { useState } from 'react';
import { 
  CalendarDays, 
  Clock, 
  FileText, 
  Image, 
  Download, 
  Pill, 
  Bell,
  User,
  DollarSign,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { format, parseISO } from 'date-fns';
import { TreatmentSession } from '@/types/treatmentSession';
import { toast } from 'sonner';

type SessionDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  session: TreatmentSession;
};

const SessionDetailsModal: React.FC<SessionDetailsModalProps> = ({
  isOpen,
  onClose,
  session
}) => {
  const [doctorNotes, setDoctorNotes] = useState(session.doctorNotes || '');
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  const handleSaveNotes = () => {
    // Here you would typically call an API to save the notes
    toast.success('Notes saved successfully');
  };

  const handleMarkFollowUp = () => {
    // Here you would typically call an API to save the follow-up flag
    setFollowUpNeeded(!followUpNeeded);
    toast.success(followUpNeeded 
      ? 'Follow-up flag removed' 
      : 'Session marked for follow-up'
    );
  };

  const handleDownloadDocument = (docId: number) => {
    // Here you would typically download the document
    toast.success('Document download started');
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case 'unpaid':
        return (
          <Badge className="bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Unpaid
          </Badge>
        );
      case 'partial':
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            <DollarSign className="h-3 w-3 mr-1" />
            Partial
          </Badge>
        );
      default:
        return (
          <Badge>
            {status}
          </Badge>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center">
            Session #{session.id} - {session.title}
          </DialogTitle>
          <DialogDescription>
            <div className="flex items-center mt-1 text-sm text-muted-foreground">
              <CalendarDays className="h-4 w-4 mr-1" />
              <span>{format(parseISO(session.date), 'PPPP')}</span>
              <Clock className="h-4 w-4 ml-3 mr-1" />
              <span>{format(parseISO(session.date), 'p')}</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="details">Session Details</TabsTrigger>
            <TabsTrigger value="files">Files & Documents</TabsTrigger>
            <TabsTrigger value="notes">Doctor's Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="pt-4">
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-sm font-semibold">Patient Information</h3>
                  <div className="flex items-center mt-2">
                    <User className="h-4 w-4 mr-2 text-muted-foreground" />
                    <a href={`/doctor-area/case-file/${session.doctorId}`} className="text-primary hover:underline">
                      {session.doctorName}
                    </a>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Session #{session.sessionNumber} of Treatment Plan #{session.planId}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold">Payment Status</h3>
                  <div className="mt-2">
                    {getPaymentStatusBadge(session.paymentStatus)}
                  </div>
                  {session.paymentReceipt && (
                    <p className="text-sm mt-1 flex items-center">
                      <DollarSign className="h-3 w-3 mr-1" /> 
                      {session.paymentReceipt.amount} {session.paymentReceipt.currency}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Session Description</h3>
                <p className="text-sm">
                  {session.description || 'No description provided for this session.'}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Attendance</h3>
                <p className="text-sm flex items-center">
                  {session.patientAttended ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                      Patient attended this session
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-1 text-red-500" />
                      Patient did not attend this session
                    </>
                  )}
                </p>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center">
                  <Switch 
                    id="follow-up" 
                    checked={followUpNeeded}
                    onCheckedChange={handleMarkFollowUp}
                  />
                  <Label htmlFor="follow-up" className="ml-2">
                    <Bell className="h-4 w-4 mr-1 inline" />
                    Mark follow-up needed
                  </Label>
                </div>
                
                <Button variant="outline" onClick={() => toast.info('Treatment plan view not implemented')}>
                  View Full Treatment Plan
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="files" className="pt-4">
            <div className="space-y-6">
              {/* Documents Section */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Documents</h3>
                {session.documents && session.documents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {session.documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 border rounded-md">
                        <div className="flex items-center">
                          <FileText className="h-5 w-5 mr-2 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium">{doc.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {doc.type.replace('_', ' ').toUpperCase()} • Uploaded {format(parseISO(doc.uploadedAt), 'PP')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownloadDocument(doc.id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No documents uploaded for this session.</p>
                )}
              </div>

              {/* Photos Section */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Photos</h3>
                {session.photos && session.photos.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {session.photos.map(photo => (
                      <div key={photo.id} className="border rounded-md overflow-hidden">
                        <div className="aspect-video bg-gray-100 relative">
                          <img 
                            src={photo.fileUrl} 
                            alt={photo.description || `Photo ${photo.id}`}
                            className="w-full h-full object-cover"
                          />
                          <Badge 
                            className="absolute top-2 left-2"
                            variant={
                              photo.type === 'before' ? 'outline' :
                              photo.type === 'after' ? 'default' : 'secondary'
                            }
                          >
                            {photo.type.charAt(0).toUpperCase() + photo.type.slice(1)}
                          </Badge>
                        </div>
                        <div className="p-2">
                          <p className="text-xs text-muted-foreground">
                            Uploaded {format(parseISO(photo.uploadedAt), 'PP')}
                          </p>
                          {photo.description && (
                            <p className="text-sm mt-1">{photo.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No photos uploaded for this session.</p>
                )}
              </div>

              {/* Prescription Section (placeholder) */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Prescription</h3>
                <div className="p-4 border rounded-md">
                  <div className="flex items-center">
                    <Pill className="h-5 w-5 mr-2 text-purple-500" />
                    <p className="text-sm">No prescription uploaded for this session.</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="notes" className="pt-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-2">Doctor's Notes</h3>
                <Textarea 
                  value={doctorNotes} 
                  onChange={(e) => setDoctorNotes(e.target.value)}
                  placeholder="Enter clinical notes about this session..."
                  className="min-h-[150px]"
                />
                <Button 
                  onClick={handleSaveNotes}
                  className="mt-2"
                >
                  Save Notes
                </Button>
              </div>

              {session.clinicNotes && (
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-semibold mb-2">Clinic Notes</h3>
                  <p className="text-sm p-3 bg-muted rounded-md">
                    {session.clinicNotes}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionDetailsModal;
